if (!customElements.get('dynamic-pricing-datepicker')) {
  customElements.define(
    'dynamic-pricing-datepicker',
    class DynamicPricingDatepicker extends HTMLElement {
      constructor() {
        super();

        this.fallback = window.FALLBACK_PRICES;
        // Initialize selected dates as element properties
        this.selectedStartDate = this.normalizeDate(new Date());
        this.selectedEndDate = this.normalizeDate(new Date());
        this.firstLoad = true;
        this.dpDays = 1;
        this.quantity = 1;
        this.discount = this.dataset.disc;
      }
      normalizeDate(date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      }
      connectedCallback() {
        this.modal = this.querySelector('.datepicker-modal');
        this.popup = this.modal.querySelector('.datepicker-popup');
        this.gorgias = document.querySelector('#gorgias-chat-container');
        this.formId = this.dataset.formId;
        this.form = document.getElementById(this.formId);

        // Setup quantity element once DOM is fully loaded
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.setupQuantityElement());
        } else {
          // DOM already loaded
          this.setupQuantityElement();
        }

        this.handleDatePicker();
        this.isMobile();
        this.perDayPriceContainer = document.querySelectorAll('.perday-dp-price');
        this.innerFooterContainer = document.querySelectorAll('.datepicker-footer-inner');
        this.saleContainers = document.querySelectorAll('.dynamic-price-sale');

        this.handlePriceUpdate(1, this.selectedStartDate, this.selectedEndDate);

        // Remove highlighting
        this.querySelectorAll('button.selected').forEach((btn) => {
          btn.classList.remove(
            'selected',
            'selected-start-date',
            'selected-end-date',
            'in-range',
            'range-selected',
          );
        });

        if (this.form && !this.formSubmitBound) {
          this.formSubmitBound = true; // prevent double binding

          this.form.addEventListener('submit', () => {
            this.customEventDatePicker('form_submit', this.quantity, this.dpDays);
          });
        }
      }

      customEventDatePicker(type, quantity, days) {
        const endpoint = 'https://portal.simify.com/api/v2/webhooks/customEvent';
        const daysAndQuantity = quantity && days ? `Days: ${days} | Quantity: ${quantity}` : null;

        const payload = {
          eventSource: 'cal_view_datepicker',
          eventType: type,
          additionalInfo: daysAndQuantity,
        };

        const body = JSON.stringify(payload);

        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'text/plain' });
          const sent = navigator.sendBeacon(endpoint, blob);
          if (sent) return;
        }

        // Fallback: fetch()
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json().catch(() => null);
          })
          .catch((error) => {
            console.error('Error tracking custom event:', error);
          });
      }

      setupQuantityElement() {
        this.quantityEl = document.querySelector(`input[form=${this.formId}]`);

        if (this.quantityEl) {
          // this.quantityEl.readOnly = true;
          // this.quantityEl.style.pointerEvents = 'none';
          this.quantityEl.setAttribute('tabindex', '-1');

          const handler = () => {
            if (this.quantityEl.value <= 0) this.quantityEl.value = 1;
            this.quantity = this.quantityEl.value;
            this.dpDays =
              Math.round((this.selectedEndDate - this.selectedStartDate) / (1000 * 60 * 60 * 24)) +
              1;
            this.handlePriceUpdate(this.dpDays, this.selectedStartDate, this.selectedEndDate);
          };

          // Add event listener for quantity changes
          this.quantityEl.addEventListener('change', handler);
          this.quantityEl.addEventListener('inputReset', handler);
        }
      }

      handlePriceUpdate(dpDays, startDate, endDate) {
        const form = document.getElementById(this.formId);
        if (!form) return;

        const currency = window.cartCurrency;
        const days = parseInt(dpDays, 10);
        if (days < 1) {
          console.error('Days out of band. Seting to 1');
          days = 1;
        } else if (days > 180) {
          console.error('Days out of band. Setting to 180');
          days = 180;
        }

        const category = this.dataset.category;
        const variantId = this.dataset.variantId;

        const pricingModel = window.pricing[category] ?? this.fallback;
        const amount = pricingModel[days - 1];

        const exchangeRate = window.exchangeTable[currency];
        const price = exchangeRates(amount, exchangeRate, currency);
        const quantity = parseInt(this.quantityEl.value, 10) ?? 1;
        // this.discount will be great than 0 if theres a sale, otherwise it will be 0
        // the discountMultiplier will be 1 if there is no sale, otherwise it will be between 0 and 1 e.g. 0.75 for a 25% off sale
        const discountMultiplier = (100 - this.discount) / 100;

        // The price above the buy buttons should always take quantity and discount multiplier into account
        // The price inside the datepicker should only take discount multiplier into account

        // Compare at price (without discount)
        const totalPrice = price * quantity;

        // Discount price
        const salePrice = price * discountMultiplier;

        if (price !== salePrice) {
          this.isOnSale = true;
        }

        // update product form hidden inputs
        const idInput = form.querySelector('input[name="id"]');
        const dpDaysInput = form.querySelector('input[name="properties[dp-days]"]');
        const startDateInput = form.querySelector('input[name="properties[startDate]"]');
        const endDateInput = form.querySelector('input[name="properties[endDate]"]');
        const calInput = document.getElementById('cal-view-date-picker');

        if (idInput) idInput.value = variantId;
        if (dpDaysInput) dpDaysInput.value = days;
        if (startDateInput) startDateInput.value = startDate.toLocaleDateString('sv-SE');
        if (endDateInput) endDateInput.value = endDate.toLocaleDateString('sv-SE');

        [idInput, dpDaysInput, startDateInput, endDateInput].forEach((input) => {
          if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const formattedPrice = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        });

        const format = (value) =>
          `${Shopify.currency.active.slice(0, 2)}${formattedPrice
            .format(value)
            .replace(/\.00$/, '')}`;

        // The price above the buy buttons should always take quantity and discount multiplier into account
        // The price inside the datepicker should only take discount multiplier into account

        const setPrices = (selector, value) => {
          document.querySelectorAll(selector).forEach((el) => {
            el.innerHTML = format(value);
          });
        };

        // Sale pricing
        if (this.isOnSale) {
          this.saleContainers.forEach((el) => {
            el.classList.contains('dp-buy-buttons')
              ? (el.innerHTML = format(salePrice * quantity))
              : (el.innerHTML = format(salePrice));
            // el.innerHTML = format(salePrice);
          });
        }

        // Total pricing (never discounted here)
        setPrices('.total-pricing .dynamic-price', totalPrice);
        setPrices('.per-day-pricing .dynamic-price', price);

        // Per-day calculation
        let perDayPrice = Number((price / days).toFixed(2)) * discountMultiplier;

        this.perDayPriceContainer.forEach((el) => {
          const perEsim = quantity > 1 && el.closest('.total-pricing') ? '/eSIM' : '';
          el.innerText = `${formattedPrice.format(perDayPrice).replace(/\.00$/, '')}/day${perEsim}`;
        });

        // Footer visibility
        this.innerFooterContainer.forEach((el) => {
          el.style.display = 'flex';
        });

        calInput.value = days > 1 ? `${days} days` : '1 day';
      }

      isMobile() {
        return window.matchMedia('(max-width: 768px)').matches;
      }

      handleDatePicker() {
        const root = this;
        const normalizeDate = this.normalizeDate.bind(this);
        const datepicker = this.querySelector('.datepicker');
        const dateInput = this.querySelector('.date-input');
        const yearInput = datepicker.querySelector('.year-input');
        const monthInput = datepicker.querySelector('.month-input');
        const applyBtn = datepicker.querySelector('.apply');
        const nextBtn = datepicker.querySelector('.next');
        const prevBtn = datepicker.querySelector('.prev');
        const confirmDaysSpans = document.querySelectorAll('.confirm-days');
        const datepickerContainer = this.querySelector('.datepicker-container');

        const today = new Date();

        let selectedDate = new Date();
        let year = selectedDate.getFullYear();
        let month = selectedDate.getMonth();

        function renderMonth(container, y, m) {
          const lastOfPrevMonth = new Date(y, m, 0);
          const lastOfMonth = new Date(y, m + 1, 0);

          // leading blanks
          for (let i = 0; i < lastOfPrevMonth.getDay(); i++) {
            const blank = document.createElement('button');
            blank.disabled = true;
            container.appendChild(blank);
          }

          // actual days
          for (let d = 1; d <= lastOfMonth.getDate(); d++) {
            const button = document.createElement('button');
            const buttonText = document.createElement('span');
            buttonText.textContent = d;
            button.appendChild(buttonText);
            const thisDate = new Date(y, m, d);
            const today = normalizeDate(new Date());
            const normalizedDate = normalizeDate(thisDate);
            const maxSelectable = new Date(today);
            maxSelectable.setDate(maxSelectable.getDate() + 180 - 1);

            if (normalizedDate >= today) {
              button.ariaLabel = thisDate.toLocaleDateString('sv-SE');
              if (normalizedDate.getTime() === today.getTime()) {
                button.classList.add('today');
              }
            }

            if (normalizedDate < today || normalizedDate > maxSelectable) {
              button.disabled = true;
            }

            button.addEventListener('click', handleDateClick);
            container.appendChild(button);
          }
        }

        const displayDates = () => {
          prevBtn.disabled = today.getMonth() === month && today.getFullYear() === year;
          const labelCurrent = datepicker.querySelector('.label-current');
          const labelNext = datepicker.querySelector('.label-next');

          const currentMonthDiv = datepicker.querySelector('.dates.current-month');
          const nextMonthDiv = datepicker.querySelector('.dates.next-month');

          currentMonthDiv.innerHTML = '';
          nextMonthDiv.innerHTML = '';

          // Always show 1st month
          renderMonth(currentMonthDiv, year, month);

          // MOBILE: hide next-month entirely
          if (this.isMobile()) {
            if (labelNext) labelNext.style.display = 'none';
            if (nextMonthDiv) nextMonthDiv.style.display = 'none';
          }
          // DESKTOP: show both months
          else {
            if (labelNext) {
              labelNext.style.display = 'block';
              labelNext.textContent = new Date(year, month + 1).toLocaleString('default', {
                month: 'long',
                year: 'numeric',
              });
            }
            if (nextMonthDiv) {
              nextMonthDiv.style.display = 'grid';
            }

            renderMonth(nextMonthDiv, month === 11 ? year + 1 : year, (month + 1) % 12);
          }

          // Always update the main visible month label
          if (labelCurrent) {
            labelCurrent.textContent = new Date(year, month).toLocaleString('default', {
              month: 'long',
              year: 'numeric',
            });
          }

          highlightSelectedDates(root.selectedStartDate, root.selectedEndDate);
        };

        function highlightSelectedDates(startDate, endDate) {
          const buttons = datepicker.querySelectorAll('.dates-wrapper button:not([disabled])');
          buttons.forEach((btn) => {
            const date = normalizeDate(new Date(btn.ariaLabel));
            btn.classList.remove(
              'selected',
              'selected-start-date',
              'selected-end-date',
              'in-range',
              'range-selected',
            );
            const dateTime = date.getTime();
            const startTime = startDate?.getTime() ?? null;
            const endTime = endDate?.getTime() ?? null;
            if (startTime && dateTime === startTime) {
              btn.classList.add('selected', 'selected-start-date');
              if (endTime && endTime !== startTime) {
                btn.classList.add('range-selected');
              }
            }
            if (endTime && dateTime === endTime) {
              btn.classList.add('selected', 'selected-end-date');
              if (startTime && endTime !== startTime) {
                btn.classList.add('range-selected');
              }
            }
            if (startDate && endDate && date > startDate && date < endDate) {
              btn.classList.add('in-range');
            }
          });
        }

        const handleDateClick = (e) => {
          const allButtons = datepicker.querySelectorAll('button[aria-label]:not([disabled])');
          allButtons.forEach((btn) => {
            btn.classList.remove(
              'selected',
              'selected-start-date',
              'selected-end-date',
              'in-range',
              'range-selected',
            );
            // btn.removeAttribute('disabled');
          });
          const button = e.target.closest('button');
          const buttonDate = normalizeDate(new Date(button.ariaLabel));

          if (this.firstLoad || root.selectedEndDate > root.selectedStartDate) {
            root.selectedStartDate = buttonDate;
            root.selectedEndDate = buttonDate;
            this.firstLoad = false;
          } else if (buttonDate < root.selectedStartDate) {
            root.selectedStartDate = buttonDate;
          } else {
            root.selectedEndDate = buttonDate;
          }

          highlightSelectedDates(root.selectedStartDate, root.selectedEndDate);
          applyBtn.disabled = !root.selectedStartDate || !root.selectedEndDate;
          if (applyBtn.disabled) {
            confirmDaysSpans.forEach((s) => {
              s.textContent = '';
            });
            root.innerFooterContainer.forEach((el) => {
              el.style.display = 'none';
            });
          }

          if (root.selectedStartDate && root.selectedEndDate) {
            // datepicker.querySelectorAll('button[aria-label]').forEach((btn) => {
            //   btn.removeAttribute('disabled');
            // });
            this.dpDays =
              Math.round((root.selectedEndDate - root.selectedStartDate) / (1000 * 60 * 60 * 24)) +
              1;
            if (this.dpDays && confirmDaysSpans) {
              confirmDaysSpans.forEach((s) => {
                s.textContent = `${this.dpDays} day${this.dpDays > 1 ? 's' : ''} ${s.closest('.apply') ? `-` : ''} `;
              });
              root.handlePriceUpdate(this.dpDays, root.selectedStartDate, root.selectedEndDate);
            }
          }
        };

        dateInput.addEventListener('click', () => {
          // move datepicker into popup
          this.popup.appendChild(datepicker);
          datepicker.hidden = false;
          this.customEventDatePicker('open');

          document.body.appendChild(this.modal);

          this.modal.hidden = false;
          // document.body.style.overflow = 'hidden';

          const scrollY = window.scrollY;
          document.body.style.position = 'fixed';
          document.body.style.top = `-${scrollY}px`;
          document.body.style.width = '100%';
          document.body.style.overflow = 'hidden';
          this.scrollY = scrollY; // Store scroll position for restore
        });
        document.addEventListener('click', (e) => {
          const clickedBackdrop = e.target === this.modal;
          const clickedPopupClose = e.target.closest('.datepicker-popup-close');
          const clickedApply = e.target.closest('.apply') && !applyBtn.disabled;
          if (clickedBackdrop || clickedApply || clickedPopupClose) {
            this.modal.hidden = true;
            this.appendChild(this.modal);
            datepickerContainer.appendChild(datepicker);
            datepicker.hidden = true;
            // Unlock body scroll - restore scroll position
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            document.body.classList.remove('modal-open');

            if (clickedApply) {
              this.customEventDatePicker('confirm', this.quantity, this.dpDays);
            }

            // Restore scroll position
            if (this.scrollY !== undefined) {
              window.scrollTo(0, this.scrollY);
              this.scrollY = undefined;
            }
          }
        });
        nextBtn.addEventListener('click', () => {
          if (month === 11) year++;
          month = (month + 1) % 12;
          displayDates();
        });
        prevBtn.addEventListener('click', () => {
          if (month === 0) year--;
          month = (month - 1 + 12) % 12;
          displayDates();
        });
        monthInput.addEventListener('change', (e) => {
          e.stopPropagation();
          month = monthInput.selectedIndex;
          displayDates();
        });
        yearInput.addEventListener('change', (e) => {
          e.stopPropagation();
          year = Math.min(
            2100,
            Math.max(1900, parseInt(yearInput.value, 10) || new Date().getFullYear()),
          );
          yearInput.value = year;
          displayDates();
        });

        displayDates();
      }
    },
  );
}
